import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { kDefaultFolderListLimit, kUserFilesFoldersApiBasePath } from '@/user_files/folders/constants'
import { type DriveListSort, type DriveSortDirection, kDefaultDriveListSort, kFolderFilesSortByParam } from '@/user_files/sorting'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { FileListResponse, FileListResponseSchema } from '../schemas'
import { isAbortError } from '@/util/errors/is-abort-error'

// Stopgap safety cap: keep automatic preloading under roughly 40,000 entries
// until the drive either virtualizes large lists (and this can expand) or
// switches to true UI pagination (and this limit can be removed).
const kMaxFolderFilesPaginationPages = 80
const kFolderFilesPaginationRequestDelayMs = 50

async function getFolderFilesPage(params: {
  cursor: string | null
  folderId: string
  limit: number
  pageIndex: number
  recursive?: boolean
  signal?: AbortSignal
  sortBy: string
  sortOrder: DriveSortDirection
  statuses?: UserFileProcessingStatus[]
  sentryTransactionId: string
  token: string
}): Promise<FileListResponse> {
  // Repeated on every page: the API encodes the sort inside the cursor and rejects a cursor
  // replayed under a different sort.
  const searchParams = new URLSearchParams({
    limit: String(params.limit),
    sort_by: params.sortBy,
    sort_order: params.sortOrder,
  })
  params.statuses?.forEach((status) => {
    searchParams.append('statuses', status)
  })
  if (params.cursor) {
    searchParams.set('cursor', params.cursor)
  }
  if (params.recursive) {
    searchParams.set('recursive', 'true')
  }
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/${encodeURIComponent(params.folderId)}/files?${searchParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    signal: params.signal,
    headers: {
      Authorization: `Bearer ${params.token}`,
      'X-Transaction-Id': params.sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)

    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          responseData: errorData,
          parseError: parseError ? formatUnknownError(parseError) : null,
        },
      })
    })

    throw new ApiError(response.status, detail || `Failed to fetch folder files: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder files response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder files response JSON')
  }

  const parsed = FileListResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder files response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder files response shape', parsed.error.issues)
  }

  return parsed.data
}

/**
 * Get files within a folder, in the order the backend returns them.
 *
 * @param folderId - The folder ID (UUID)
 * @param options.limit - Page size for backend pagination (1-500)
 * @param options.recursive - Include files in descendant folders
 * @param options.signal - Optional signal used to abort the request
 * @param options.sort - Column and direction to sort by; defaults to filename ascending
 * @param options.statuses - Optional repeated statuses query params
 * @returns FileListResponse containing the files fetched
 * @throws Error when user is not authenticated
 * @throws ApiError when the folder-files request fails
 */
export async function getFolderFiles(
  folderId: string,
  options?: {
    limit?: number
    recursive?: boolean
    signal?: AbortSignal
    sort?: DriveListSort
    statuses?: UserFileProcessingStatus[]
  }
): Promise<FileListResponse> {
  const sentryTransactionId = nanoid()
  const limit = options?.limit ?? kDefaultFolderListLimit
  const recursive = options?.recursive
  const signal = options?.signal
  const sort = options?.sort ?? kDefaultDriveListSort
  const sortBy = kFolderFilesSortByParam[sort.key]
  const statuses = options?.statuses

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const allFiles: FileListResponse['files'] = []
    let nextCursor: string | null = null
    let pageIndex = 1
    const seenCursors = new Set<string>()

    do {
      if (pageIndex > kMaxFolderFilesPaginationPages) {
        // The drain loop treats the safety cap as a failure rather than silently truncating.
        throw new Error(`Exceeded ${kMaxFolderFilesPaginationPages} pages while fetching folder files`)
      }

      if (nextCursor) {
        if (seenCursors.has(nextCursor)) {
          throw new Error('Encountered a repeated pagination cursor while fetching folder files')
        }

        seenCursors.add(nextCursor)
      }

      const page = await getFolderFilesPage({
        cursor: nextCursor,
        folderId,
        limit,
        pageIndex,
        recursive,
        signal,
        sortBy,
        sortOrder: sort.direction,
        statuses,
        sentryTransactionId,
        token,
      })

      allFiles.push(...page.files)
      nextCursor = page.next_cursor
      pageIndex += 1

      if (nextCursor && pageIndex <= kMaxFolderFilesPaginationPages) {
        await new Promise((resolve) => setTimeout(resolve, kFolderFilesPaginationRequestDelayMs))
      }
    } while (nextCursor)

    return {
      files: allFiles,
      folder_id: folderId,
      // The drain loop exits with the listing exhausted, so it reports no continuation. A bounded
      // caller gets the live cursor back so it can ask for the next page.
      next_cursor: null,
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error
    }

    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            folderId,
            limit,
            recursive: recursive ?? false,
            sortBy,
            sortOrder: sort.direction,
            statuses,
          },
        })
      })
    }
    throw error
  }
}
